import { useEffect, useMemo, useRef } from 'react';

import { useTeamData } from '@confluence/permissions-principals/entry-points/useTeamData.ts';

import type { PublishModalRestrictionsInput } from './publishModalDataToQueriesState.ts';
import type { useQueriesShareAndRestrictDialogQuery as useQueriesShareAndRestrictDialogQuery$data } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import { ConfluenceGroupUsageType } from './__types__/useQueriesShareAndRestrictDialogQuery.ts';
import type { useContentPermissionsQuery$data as ContentPermissionsData } from './__generated__/useContentPermissionsQuery.graphql.ts';

export type UseTeamDataResult = Omit<ReturnType<typeof useTeamData>, 'refetch'>;

export const useShareDialogTeamDataHandler = (
	cloudId: string,
	publishModalRestrictions?: PublishModalRestrictionsInput,
	data?: useQueriesShareAndRestrictDialogQuery$data,
	contentPermissionsData?: ContentPermissionsData,
	shouldUseNewQuery?: boolean,
): UseTeamDataResult => {
	const { teamData, isTeamDataLoading, teamDataError, refetch } = useTeamData(cloudId);

	const teamIds = useMemo(() => {
		const teamIdSet = new Set<string>();

		// Extract team IDs from publish modal restrictions
		// Using flatMap to flatten, then single forEach to add matching items
		const publishModalGroups = publishModalRestrictions?.flatMap(
			(item) => item?.restrictions?.group?.results ?? [],
		);
		publishModalGroups?.forEach((group) => {
			if (group?.id && group.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
				teamIdSet.add(group.id);
			}
		});

		// New query path
		const contentPermissionNodes =
			shouldUseNewQuery &&
			contentPermissionsData?.confluence_contentPermissions?.confluencePrincipalsConnection?.nodes;
		if (contentPermissionNodes) {
			contentPermissionNodes.forEach((node) => {
				if (
					node?.principalId &&
					node.principalType === 'GROUP' &&
					node.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION
				) {
					teamIdSet.add(node.principalId);
				}
			});
			return Array.from(teamIdSet);
		}

		// Legacy path (share dialog)
		if (data?.content?.nodes?.[0]?.restrictions) {
			const readNodes = data.content.nodes[0].restrictions.read?.restrictions?.group?.nodes ?? [];
			const updateNodes =
				data.content.nodes[0].restrictions.update?.restrictions?.group?.nodes ?? [];
			const allNodes = [...readNodes, ...updateNodes];
			allNodes.forEach((node) => {
				if (node?.id && node?.usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
					teamIdSet.add(node.id);
				}
			});
		}

		return Array.from(teamIdSet);
	}, [publishModalRestrictions, data, contentPermissionsData, shouldUseNewQuery]);

	// Track previous teamIds to avoid unnecessary refetches
	const prevTeamIdsRef = useRef<string[]>([]);

	useEffect(() => {
		// Only refetch if teamIds have changed
		const teamIdsChanged =
			teamIds.length !== prevTeamIdsRef.current.length ||
			teamIds.some((id, i) => id !== prevTeamIdsRef.current[i]);

		if (teamIds.length > 0 && teamIdsChanged) {
			prevTeamIdsRef.current = teamIds;
			// Convert team IDs to ARI format for AGG query
			const ariTeamIds = teamIds.map((id) => `ari:cloud:identity::team/${id}`);
			void refetch({
				teamIds: ariTeamIds,
				siteId: cloudId,
			});
		}
	}, [teamIds, cloudId, refetch]);

	return {
		teamData,
		isTeamDataLoading,
		teamDataError,
	};
};
